import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sports } from "@/api/sports";
import { AuthExpiredError } from "@/api/client";
import { PageHeader, TeamBadge, Skeleton } from "@/components/ui-kit";
import { DataState, SourceNote } from "@/components/DataState";
import { SportsNav, LeagueToggle, SeasonSelect } from "@/components/SportsControls";
import { CURRENT_SEASON, groupNflTeams, ncaaConferences, ncaaConferenceLabel, teamsByNcaaConference, formatRecord } from "@/lib/sports";

function TeamRow({ team }) {
  return (
    <Link to={`/team/${encodeURIComponent(team.id)}`} data-testid={`team-card-${team.id}`}
      className="flex items-center gap-3 tt-panel rounded-lg px-3 py-2.5 hover:border-[#3A4859] transition-colors">
      <TeamBadge team={team} size={34} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">{team.name}</div>
        {team.location && <div className="text-[11px] text-white/40 truncate">{team.location}</div>}
      </div>
      {formatRecord(team.record) && <span className="text-xs text-white/50 tabnums shrink-0">{formatRecord(team.record)}</span>}
    </Link>
  );
}

function NflTeams({ teams }) {
  const groups = useMemo(() => groupNflTeams(teams), [teams]);
  if (!groups.length) return <DataState state="empty" title="No NFL teams" detail="No provider teams to show." />;
  return (
    <div className="space-y-8" data-testid="nfl-teams">
      {groups.map((conf) => (
        <section key={conf.conference}>
          <h2 className="font-display tracking-wide text-lg text-white mb-3">{conf.conference}</h2>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {conf.divisions.map((d) => (
              <div key={d.division} data-testid={`nfl-div-${conf.conference}-${d.division}`}>
                <div className="font-display tracking-widest text-xs text-white/40 mb-2">{conf.conference} {d.division}</div>
                <div className="space-y-2">{d.teams.map((t) => <TeamRow key={t.id} team={t} />)}</div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function NcaaTeams({ teams }) {
  const confs = useMemo(() => ncaaConferences(teams), [teams]);
  const [conf, setConf] = useState(null);
  const active = conf ?? confs[0];
  if (!confs.length) return <DataState state="empty" title="No NCAA teams" detail="No provider teams to show." />;
  const list = teamsByNcaaConference(teams, active);
  return (
    <div data-testid="ncaa-teams">
      <div className="flex flex-wrap gap-2 mb-5 max-h-40 overflow-y-auto tt-scroll" data-testid="ncaa-conf-picker">
        {confs.map((c) => (
          <button key={c} onClick={() => setConf(c)} data-testid={`ncaa-conf-${c}`}
            className={`px-3 h-8 rounded-full text-xs transition-colors ${String(active) === String(c) ? "bg-[#24362A] text-white border border-[#2F7D32]" : "tt-panel text-white/55 hover:text-white"}`}>
            {ncaaConferenceLabel(c, teams)}
          </button>
        ))}
      </div>
      <h2 className="font-display tracking-wide text-lg text-white mb-3">{ncaaConferenceLabel(active, teams)}</h2>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{list.map((t) => <TeamRow key={t.id} team={t} />)}</div>
    </div>
  );
}

export default function TeamsPage() {
  const [league, setLeague] = useState("NFL");
  const [season, setSeason] = useState(CURRENT_SEASON);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Sports.teams(league, season)
      .then(setRes)
      .catch((e) => { if (!(e instanceof AuthExpiredError)) setRes({ state: "error", teams: [] }); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league, season]);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Teams" subtitle="NFL & NCAA teams from the live provider feed." right={<SeasonSelect value={season} onChange={setSeason} />} />
      <SportsNav />
      <div className="mb-6"><LeagueToggle value={league} onChange={setLeague} /></div>

      {loading ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : res?.state === "ok" ? (
        <>
          {league === "NFL" ? <NflTeams teams={res.teams} /> : <NcaaTeams teams={res.teams} />}
          <SourceNote timestamp={res.source_timestamp} coverage="Live provider feed" />
        </>
      ) : (
        <DataState state={res?.state || "error"} onRetry={load}
          title={res?.state === "feature_unavailable" ? "Teams are being connected" : undefined} />
      )}
    </div>
  );
}
